/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable eqeqeq */
// ---------------------------------------------------------------------------------------------
// EPOD Phase 3 - Dealer Confirmation / NDV (Action 15, WEB-017..022 ONLY).
//
// Genuinely new screen - no prior Dealer Confirmation UI existed anywhere in this repo (verified).
// Deliberately a SEPARATE file from epodScreen.js (already 2700+ lines, LSP/AL-facing) since this
// is a different audience (the dealer / CUSTOMER user_type) doing a different job.
//
// Reuses established patterns rather than inventing new ones:
//   - SweetAlert for success/failure messages (react-bootstrap-sweetalert, as in epodScreen.js)
//   - redirectURL + { Authorization: localStorage.getItem("userid") } (as in BillingTripsComponent.js)
//   - The centered-overlay confirmation modal pattern (Shortage remarks / Damage images), copied
//     from epodScreen.js's Reject-E-POD modal
//   - AgGrid for the LR list, same library already used throughout billing/*
//
// The UI never assumes a submission succeeded - every action re-fetches the authoritative queue
// from the backend afterward (guide section 33: refresh authoritative state, don't rely on
// optimistic local state).
//
// WEB-025 (config-driven reopening) - a Reopen button appears on an already-confirmed row ONLY
// when the backend's own /dealerConfirmationOptions response says dealer_reopen_enabled is true
// for this caller; the backend independently re-validates everything on the actual reopen call
// regardless of what this UI shows, so a stale/tampered client can never reopen something the
// config forbids. Admin reopening has no UI here - this screen is scoped to the dealer's own
// LRs (see listQueue's scope resolution), and there is no existing Admin-facing EPOD screen in
// this codebase to extend; see the WEB-025 completion report for that open item.
// ---------------------------------------------------------------------------------------------
import React, { Component } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import "@ag-grid-enterprise/all-modules/dist/styles/ag-grid.css";
import "@ag-grid-enterprise/all-modules/dist/styles/ag-theme-balham.css";
import SweetAlert from 'react-bootstrap-sweetalert';
import { getWorkflowDisplayStatus, STATUS_BADGE_CLASS } from "./workflowStatus";
import { isAwaitingDealer, dealerSeqOf } from "./epodDealerFlow";
var moment = require('moment');
var redirectURL = require('../redirectURL');
var { withDeptCode } = require('../common/deptCode'); // SLA hints for the page's ?dept_code (none = SPD)

const authHeaders = () => ({ headers: { 'Authorization': localStorage.getItem("userid") } });

function fmtDate(v) {
    if (!v) return '';
    try { return moment(v).format('DD-MMM-YYYY HH:mm'); } catch (e) { return ''; }
}

// Never handled al_approved/al_rejected (AL's own decision) - those fell through to the
// epod_gate_status branch/blank, so a rejected row showed nothing meaningful and never red. Those
// two states are now resolved via the centralized workflowStatus.js resolver instead (see the
// Status column below) - reusing its existing wording/danger-red styling rather than growing a
// second, divergent copy of that mapping here. Every other branch (closed_clean/pending_al_review/
// gate-status fallback) is unchanged.
function friendlyStatus(row) {
    var s = row.dealer_confirmation_status;
    if (s === 'closed_clean') return 'Clean - Closed';
    if (s === 'closed_damage') return 'Damage - Closed';
    if (s === 'pending_al_review') return 'Pending AL Review (' + (row.dealer_confirmation_condition || '') + ')';
    if (isAwaitingDealer(row)) return 'Awaiting Your Confirmation';
    return row.epod_gate_status || '';
}

export default class DealerConfirmationScreen extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rowData: [],
            loading: false,
            show: false, basicTitle: '', basicType: 'success',
            options: {
                enabled: false, shortage_sla_hours: 48, max_remarks_length: 500, damage_images: { min_count: 1, max_count: 6 },
                condition_change: { dealer_reopen_enabled: false, require_reopen_remarks: true, max_reopen_count: 1 },
            },

            // WEB-025 - Reopen modal
            reopenModalShow: false, reopenRow: null, reopenRemarks: '', reopenSubmitting: false, reopenError: '',

            // Clean - mandatory confirmation modal (no free-text/files, so nothing to preserve on
            // Cancel; Cancel simply closes this with no backend call at all). Same pattern already
            // used on /epodScreen's own Dealer Condition Clean button.
            cleanModalShow: false, cleanRow: null, cleanSubmitting: false, cleanError: '',

            // Shortage modal
            shortageModalShow: false, shortageRow: null, shortageRemarks: '', shortageNdvAmount: '', shortageQuantity: '', shortageSubmitting: false, shortageError: '',
            shortageDraftRestored: false, // WEB-024 - true right after a saved draft is restored into the box
            // Damage modal
            damageModalShow: false, damageRow: null, damageRemarks: '', damageNdvAmount: '', damageFiles: [], damageSubmitting: false, damageError: '',
            damageDraftRestored: false, damageDraftImageCount: 0,
        };
        this.gridColumnApi = null;
    }

    componentDidMount() {
        this.loadOptions();
        this.loadQueue();
    }

    loadOptions = () => {
        redirectURL.post("/billing/dealerConfirmationOptions", withDeptCode({}), authHeaders())
            .then((resp) => {
                if (resp.data && resp.data.status === "success") this.setState({ options: resp.data });
            })
            .catch(() => { /* fail closed - options stay at the disabled default */ });
    }

    loadQueue = () => {
        this.setState({ loading: true });
        redirectURL.post("/billing/dealerConfirmationQueue", withDeptCode({}), authHeaders())
            .then((resp) => {
                if (resp.data && resp.data.status === "success") {
                    this.setState({ rowData: resp.data.rows || [], loading: false });
                } else {
                    this.setState({ loading: false, show: true, basicType: "danger", basicTitle: (resp.data && resp.data.message) || "Failed to load queue" });
                }
            })
            .catch((err) => {
                this.setState({ loading: false, show: true, basicType: "danger", basicTitle: (err.response && err.response.data && err.response.data.message) || "Failed to load queue" });
            });
    }

    closeAlert = () => { this.setState({ show: false }); }

    // ---- Clean --------------------------------------------------------------------------------
    // Clicking Clean in the grid no longer submits directly - it only opens the mandatory
    // confirmation modal below. The actual POST /billing/dealerConfirmClean call (same rowid body,
    // same auth headers as before) happens exclusively from onConfirmClean.
    onClickClean = (row) => {
        this.setState({ cleanModalShow: true, cleanRow: row, cleanSubmitting: false, cleanError: '' });
    }
    onCancelClean = () => {
        // No API call of any kind - the LR is left completely unchanged.
        this.setState({ cleanModalShow: false, cleanRow: null, cleanSubmitting: false, cleanError: '' });
    }
    onConfirmClean = () => {
        if (this.state.cleanSubmitting) return; // guards against double-click/double-submit
        var row = this.state.cleanRow;
        if (!row) return;
        this.setState({ cleanSubmitting: true, cleanError: '' });
        redirectURL.post("/billing/dealerConfirmClean", { rowid: row._id }, authHeaders())
            .then((resp) => {
                if (resp.data && resp.data.status === "success") {
                    this.setState({
                        cleanModalShow: false, cleanRow: null, cleanSubmitting: false, cleanError: '',
                        show: true, basicType: "success", basicTitle: resp.data.message || "Marked All Good.",
                    });
                    // Re-fetch authoritative state only after a confirmed success - never assume
                    // local success, but also never refetch/close on a genuine failure (previously
                    // this closed the modal and refetched unconditionally, even when the API
                    // rejected the request - leaving the dealer with no way to see why or retry).
                    this.loadQueue();
                } else {
                    // Stays open on failure, inline error shown in the still-open modal - same
                    // pattern as onConfirmShortage/onConfirmDamage/onConfirmReopen below.
                    this.setState({ cleanSubmitting: false, cleanError: (resp.data && resp.data.message) || "Failed to submit." });
                }
            })
            .catch((err) => {
                this.setState({ cleanSubmitting: false, cleanError: (err.response && err.response.data && err.response.data.message) || "Failed to submit." });
            });
    }

    // ---- WEB-024: Draft Save / Recovery ----------------------------------------------------------
    // A draft is autosaved (debounced) while the dealer types, and restored automatically the next
    // time they open the same LR's modal - covering a session timeout or an accidentally closed
    // tab before Submit was ever clicked. It never touches dealer_confirmation_status and is never
    // treated as a submission. Debounce timers live on `this`, not state, so they don't churn
    // re-renders; each is cleared before being reset, so only the LAST edit within the debounce
    // window is ever sent.
    scheduleShortageDraftSave = (row, remarks, ndvAmount, shortageQuantity) => {
        clearTimeout(this._shortageDraftTimer);
        this._shortageDraftTimer = setTimeout(() => {
            redirectURL.post("/billing/dealerConfirmationDraftSave",
                { rowid: row._id, condition: 'shortage', remarks: remarks, ndv_amount: ndvAmount, shortage_quantity: shortageQuantity }, authHeaders())
                .catch(() => { /* autosave failures are silent - never interrupt the dealer's typing */ });
        }, 1200);
    }
    scheduleDamageDraftSave = (row, remarks, files, ndvAmount) => {
        clearTimeout(this._damageDraftTimer);
        this._damageDraftTimer = setTimeout(() => {
            // Only lightweight metadata is drafted for images - never the binary file itself, so
            // no partial/abandoned upload is ever left in storage (guide section 27).
            var pendingImageMeta = (files || []).map((f) => ({ name: f.name, size: f.size, type: f.type }));
            redirectURL.post("/billing/dealerConfirmationDraftSave",
                { rowid: row._id, condition: 'damage', remarks: remarks, pending_image_meta: pendingImageMeta, ndv_amount: ndvAmount }, authHeaders())
                .catch(() => { });
        }, 1200);
    }

    // ---- Shortage -------------------------------------------------------------------------------
    onClickShortage = (row) => {
        this.setState({ shortageModalShow: true, shortageRow: row, shortageRemarks: '', shortageNdvAmount: '', shortageQuantity: '', shortageError: '', shortageSubmitting: false, shortageDraftRestored: false });
        redirectURL.post("/billing/dealerConfirmationDraftGet", { rowid: row._id }, authHeaders())
            .then((resp) => {
                var draft = resp.data && resp.data.status === "success" ? resp.data.draft : null;
                // Only restore into the Shortage box if the saved draft was itself a Shortage
                // draft - a Damage draft for this same LR is a different, independent form.
                if (draft && draft.condition === 'shortage' && this.state.shortageRow && this.state.shortageRow._id === row._id) {
                    this.setState({
                        shortageRemarks: draft.draft.remarks || '',
                        shortageNdvAmount: (draft.draft.ndv_amount !== null && draft.draft.ndv_amount !== undefined) ? String(draft.draft.ndv_amount) : '',
                        shortageQuantity: (draft.draft.shortage_quantity !== null && draft.draft.shortage_quantity !== undefined) ? String(draft.draft.shortage_quantity) : '',
                        shortageDraftRestored: true,
                    });
                }
            })
            .catch(() => { /* no draft to restore - the dealer just starts fresh, same as today */ });
    }
    onChangeShortageRemarks = (e) => {
        var v = e.target.value;
        var maxLen = this.state.options.max_remarks_length || 500;
        if (v.length > maxLen) v = v.substring(0, maxLen);
        this.setState({ shortageRemarks: v, shortageError: '', shortageDraftRestored: false });
        this.scheduleShortageDraftSave(this.state.shortageRow, v, this.state.shortageNdvAmount, this.state.shortageQuantity);
    }
    // BRD WEB-018 - dealer-entered NDV amount. Kept as a free-text-ish number input (not a stepper)
    // so a dealer can freely retype/clear it while composing, same UX as the remarks textarea;
    // validated properly (numeric, >=0) only on Submit, in onConfirmShortage below - never
    // silently coerced while typing.
    onChangeShortageNdvAmount = (e) => {
        var v = e.target.value;
        this.setState({ shortageNdvAmount: v, shortageError: '', shortageDraftRestored: false });
        this.scheduleShortageDraftSave(this.state.shortageRow, this.state.shortageRemarks, v, this.state.shortageQuantity);
    }
    // Shortage Quantity - a distinct value from NDV Amount (piece/carton count vs a rupee figure).
    // Validated properly (positive whole number) only on Submit, matching the NDV field's own UX.
    onChangeShortageQuantity = (e) => {
        var v = e.target.value;
        this.setState({ shortageQuantity: v, shortageError: '', shortageDraftRestored: false });
        this.scheduleShortageDraftSave(this.state.shortageRow, this.state.shortageRemarks, this.state.shortageNdvAmount, v);
    }
    onCancelShortage = () => {
        // No submission API call - the LR is left completely unchanged. The draft itself is
        // deliberately left as-is (not deleted) - Cancel closes the modal, it does not discard
        // notes the dealer already typed; TTL quietly cleans it up if truly abandoned, and it is
        // restored again next time this LR's Shortage modal is opened.
        clearTimeout(this._shortageDraftTimer);
        this.setState({ shortageModalShow: false, shortageRow: null, shortageRemarks: '', shortageNdvAmount: '', shortageQuantity: '', shortageError: '', shortageSubmitting: false, shortageDraftRestored: false });
    }
    onConfirmShortage = () => {
        var { shortageRow, shortageRemarks, shortageNdvAmount, shortageQuantity } = this.state;
        var trimmed = (shortageRemarks || '').trim();
        if (!trimmed) { this.setState({ shortageError: 'Remarks are required.' }); return; }
        // Client-side validation is a UX convenience only - the backend independently re-validates
        // (required, numeric, >=0, rejects NaN/Infinity/strings) and is what actually decides.
        var ndvTrimmed = String(shortageNdvAmount == null ? '' : shortageNdvAmount).trim();
        var ndvNum = Number(ndvTrimmed);
        if (ndvTrimmed === '' || isNaN(ndvNum) || !isFinite(ndvNum) || ndvNum < 0) {
            this.setState({ shortageError: 'A valid, non-negative NDV amount is required.' });
            return;
        }
        var qtyTrimmed = String(shortageQuantity == null ? '' : shortageQuantity).trim();
        var qtyNum = Number(qtyTrimmed);
        if (qtyTrimmed === '' || isNaN(qtyNum) || !isFinite(qtyNum) || !Number.isInteger(qtyNum) || qtyNum <= 0) {
            this.setState({ shortageError: 'A valid Shortage Quantity (whole number greater than 0) is required.' });
            return;
        }
        this.setState({ shortageSubmitting: true, shortageError: '' });
        redirectURL.post("/billing/dealerConfirmShortage", { rowid: shortageRow._id, remarks: trimmed, ndv_amount: ndvNum, shortage_quantity: qtyNum }, authHeaders())
            .then((resp) => {
                if (resp.data && (resp.data.status === "success" || resp.data.status === "pending_al_review" || resp.data.status.includes("NDV approved. Classified for Credit Note."))) {
                    this.setState({
                        shortageModalShow: false, shortageRow: null, shortageRemarks: '', shortageNdvAmount: '', shortageQuantity: '', shortageSubmitting: false, shortageError: '',
                        show: true, basicType: "success", basicTitle: resp.data.message,
                    });
                    this.loadQueue();
                } else {
                    this.setState({ shortageSubmitting: false, shortageError: (resp.data && resp.data.message) || 'Submission failed.' });
                }
            })
            .catch((err) => {
                this.setState({ shortageSubmitting: false, shortageError: (err.response && err.response.data && err.response.data.message) || 'Submission failed.' });
            });
    }

    // ---- Damage ---------------------------------------------------------------------------------
    onClickDamage = (row) => {
        this.setState({ damageModalShow: true, damageRow: row, damageRemarks: '', damageNdvAmount: '', damageFiles: [], damageError: '', damageSubmitting: false, damageDraftRestored: false });
        redirectURL.post("/billing/dealerConfirmationDraftGet", { rowid: row._id }, authHeaders())
            .then((resp) => {
                var draft = resp.data && resp.data.status === "success" ? resp.data.draft : null;
                if (draft && draft.condition === 'damage' && this.state.damageRow && this.state.damageRow._id === row._id) {
                    // Only the remarks, NDV amount, and a note of how many images were previously
                    // selected are restorable - the actual image FILES were never sent to the
                    // draft (metadata only), so the dealer must re-select them; the modal makes
                    // this explicit rather than silently claiming images are still attached.
                    this.setState({
                        damageRemarks: draft.draft.remarks || '',
                        damageNdvAmount: (draft.draft.ndv_amount !== null && draft.draft.ndv_amount !== undefined) ? String(draft.draft.ndv_amount) : '',
                        damageDraftRestored: true,
                        damageDraftImageCount: (draft.draft.pending_image_meta || []).length,
                    });
                }
            })
            .catch(() => { });
    }
    onChangeDamageRemarks = (e) => {
        this.setState({ damageRemarks: e.target.value, damageError: '', damageDraftRestored: false });
        this.scheduleDamageDraftSave(this.state.damageRow, e.target.value, this.state.damageFiles, this.state.damageNdvAmount);
    }
    // BRD WEB-019 - dealer-entered NDV amount for Damage, same rule/UX as Shortage's own field.
    onChangeDamageNdvAmount = (e) => {
        var v = e.target.value;
        this.setState({ damageNdvAmount: v, damageError: '', damageDraftRestored: false });
        this.scheduleDamageDraftSave(this.state.damageRow, this.state.damageRemarks, this.state.damageFiles, v);
    }
    onChangeDamageFiles = (e) => {
        var files = Array.from(e.target.files || []);
        this.setState({ damageFiles: files, damageError: '', damageDraftRestored: false });
        this.scheduleDamageDraftSave(this.state.damageRow, this.state.damageRemarks, files, this.state.damageNdvAmount);
    }
    onCancelDamage = () => {
        // Same reasoning as onCancelShortage - the draft is left in place, not deleted.
        clearTimeout(this._damageDraftTimer);
        this.setState({ damageModalShow: false, damageRow: null, damageRemarks: '', damageNdvAmount: '', damageFiles: [], damageError: '', damageSubmitting: false, damageDraftRestored: false });
    }
    onConfirmDamage = () => {
        var { damageRow, damageRemarks, damageNdvAmount, damageFiles, options } = this.state;
        var minCount = (options.damage_images && options.damage_images.min_count) || 1;
        if (damageFiles.length < minCount) {
            this.setState({ damageError: 'At least ' + minCount + ' damage image(s) are required.' });
            return;
        }
        var ndvTrimmed = String(damageNdvAmount == null ? '' : damageNdvAmount).trim();
        var ndvNum = Number(ndvTrimmed);
        if (ndvTrimmed === '' || isNaN(ndvNum) || !isFinite(ndvNum) || ndvNum < 0) {
            this.setState({ damageError: 'A valid, non-negative NDV amount is required.' });
            return;
        }
        this.setState({ damageSubmitting: true, damageError: '' });
        var formData = new FormData();
        formData.append('rowid', damageRow._id);
        formData.append('remarks', damageRemarks || '');
        formData.append('ndv_amount', ndvNum);
        damageFiles.forEach((f) => formData.append('damageImages', f));
        redirectURL.post("/billing/dealerConfirmDamage", formData, {
            headers: { 'content-type': 'multipart/form-data', 'Authorization': localStorage.getItem("userid") }
        })
            .then((resp) => {
                if (resp.data && resp.data.status === "success") {
                    this.setState({
                        damageModalShow: false, damageRow: null, damageRemarks: '', damageNdvAmount: '', damageFiles: [], damageSubmitting: false, damageError: '',
                        show: true, basicType: "success", basicTitle: resp.data.message,
                    });
                    this.loadQueue();
                } else {
                    this.setState({ damageSubmitting: false, damageError: (resp.data && resp.data.message) || 'Submission failed.' });
                }
            })
            .catch((err) => {
                this.setState({ damageSubmitting: false, damageError: (err.response && err.response.data && err.response.data.message) || 'Submission failed.' });
            });
    }

    // ---- WEB-025: Reopen ------------------------------------------------------------------------
    // Backend is authoritative regardless of what this modal shows - condition_change.enabled,
    // dealer_reopen.enabled, remarks-required, and max_reopen_count are all re-checked server-side
    // on the actual /dealerConfirmationReopen call.
    onClickReopen = (row) => {
        this.setState({ reopenModalShow: true, reopenRow: row, reopenRemarks: '', reopenError: '', reopenSubmitting: false });
    }
    onChangeReopenRemarks = (e) => {
        var v = e.target.value;
        var maxLen = this.state.options.max_remarks_length || 500;
        if (v.length > maxLen) v = v.substring(0, maxLen);
        this.setState({ reopenRemarks: v, reopenError: '' });
    }
    onCancelReopen = () => {
        this.setState({ reopenModalShow: false, reopenRow: null, reopenRemarks: '', reopenError: '', reopenSubmitting: false });
    }
    onConfirmReopen = () => {
        var { reopenRow, reopenRemarks, options } = this.state;
        var trimmed = (reopenRemarks || '').trim();
        if (options.condition_change.require_reopen_remarks && !trimmed) {
            this.setState({ reopenError: 'Remarks are required to reopen this confirmation.' });
            return;
        }
        this.setState({ reopenSubmitting: true, reopenError: '' });
        redirectURL.post("/billing/dealerConfirmationReopen", { rowid: reopenRow._id, remarks: trimmed }, authHeaders())
            .then((resp) => {
                if (resp.data && resp.data.status === "success") {
                    this.setState({
                        reopenModalShow: false, reopenRow: null, reopenRemarks: '', reopenSubmitting: false, reopenError: '',
                        show: true, basicType: "success", basicTitle: resp.data.message || "Confirmation reopened.",
                    });
                    // Refresh authoritative state - the row now shows Clean/Shortage/Damage
                    // buttons again, exactly like any other LR awaiting confirmation.
                    this.loadQueue();
                } else {
                    this.setState({ reopenSubmitting: false, reopenError: (resp.data && resp.data.message) || 'Reopen failed.' });
                }
            })
            .catch((err) => {
                this.setState({ reopenSubmitting: false, reopenError: (err.response && err.response.data && err.response.data.message) || 'Reopen failed.' });
            });
    }

    render() {
        var { rowData, options } = this.state;
        var columnDefs = [
            { headerName: "LR Number", field: "gc_waybill_no", width: 140 },
            { headerName: "Delivery Date", field: "delivery_datetime", width: 160, valueGetter: (p) => fmtDate(p.data.delivery_datetime || p.data.delivered_date) },
            {
                headerName: "Status", field: "dealer_confirmation_status", width: 220,
                valueGetter: (p) => {
                    var s = p.data.dealer_confirmation_status;
                    if (s === 'al_approved' || s === 'al_rejected') return getWorkflowDisplayStatus(p.data).text;
                    return friendlyStatus(p.data);
                },
                cellRenderer: (params) => {
                    var s = params.data.dealer_confirmation_status;
                    var span = document.createElement('span');
                    if (s === 'al_approved' || s === 'al_rejected') {
                        var resolved = getWorkflowDisplayStatus(params.data);
                        span.className = STATUS_BADGE_CLASS[resolved.style] || STATUS_BADGE_CLASS.secondary;
                        span.innerText = resolved.text;
                    } else {
                        span.innerText = friendlyStatus(params.data);
                    }
                    return span;
                },
            },
            {
                // No pre-existing dealer-remarks column exists on this screen (confirmed by
                // inspection - dealer remarks entered in the Shortage/Damage modals were never
                // surfaced back as a grid column), so this is the one, single Remarks column for
                // this screen - deliberately named plain "Remarks", not a separate "AL Remarks"
                // field, so a future dealer-remarks value has one place to join into rather than
                // duplicating this column. Only ever populated once AL has decided
                // (dealerConfirmationService.listQueue() surfaces al_review.remarks for exactly
                // those two statuses - see that function's own comment).
                headerName: "Remarks", field: "al_review_remarks", width: 240,
                valueGetter: (p) => (p.data.dealer_confirmation_status === 'al_rejected' || p.data.dealer_confirmation_status === 'al_approved') ? (p.data.al_review_remarks || '-') : '-',
            },
            { headerName: "Confirmed At", field: "dealer_confirmed_at", width: 160, valueGetter: (p) => fmtDate(p.data.dealer_confirmed_at) },
            {
                // Shortage-only - never shown for Clean/Damage rows, keeping those uncluttered.
                headerName: "Shortage Qty", field: "shortage_qty", width: 120,
                valueGetter: (p) => (p.data.dealer_confirmation_condition === 'shortage' && p.data.ndv && p.data.ndv.shortage_quantity !== undefined && p.data.ndv.shortage_quantity !== null) ? p.data.ndv.shortage_quantity : '',
            },
            {
                headerName: "Action", field: "action", width: 320,
                cellRenderer: (params) => {
                    var wrap = document.createElement('span');
                    var mk = (label, cls, handler) => {
                        var b = document.createElement('button');
                        b.className = cls; b.style.padding = '2px 8px'; b.style.fontSize = '12px'; b.style.marginRight = '4px';
                        b.innerText = label;
                        b.onclick = function (e) { e.stopPropagation(); handler(params.data); };
                        wrap.appendChild(b);
                    };

                    // A row already carrying a dealer_confirmation_status is final UNLESS WEB-025
                    // reopening is enabled for this caller - the backend re-validates all of this
                    // independently on the actual reopen call, this is purely a display decision.
                    if (params.data.dealer_confirmation_status) {
                        var cc = this.state.options.condition_change || {};
                        var reopenInfo = params.data.dealer_confirmation_reopen;
                        var usedCount = (reopenInfo && reopenInfo.upload_seq === dealerSeqOf(params.data)) ? (reopenInfo.count || 0) : 0;
                        var maxCount = cc.max_reopen_count || 1;
                        // AL has already decided (al_approved/al_rejected) - reopenConfirmation's own
                        // checkDownstreamConflict (al_review_actions has a row for EVERY AL decision,
                        // approve or reject) always fails this server-side, regardless of
                        // dealer_reopen_enabled/max_reopen_count. Known from the SAME status field
                        // already being read on this line - no new data needed. Disabled (not hidden)
                        // with a tooltip, since reopening is otherwise a real, applicable action for
                        // this dealer - it's only THIS record that's permanently past it.
                        var alreadyReviewedByAl = params.data.dealer_confirmation_status === 'al_approved' || params.data.dealer_confirmation_status === 'al_rejected';
                        if (cc.dealer_reopen_enabled && usedCount < maxCount) {
                            if (alreadyReviewedByAl) {
                                var b = document.createElement('button');
                                b.className = 'btn btn-outline-secondary'; b.style.padding = '2px 8px'; b.style.fontSize = '12px'; b.style.marginRight = '4px';
                                b.innerText = 'Reopen (' + usedCount + '/' + maxCount + ')';
                                b.disabled = true;
                                b.title = 'Action unavailable — this record has already been reviewed by the AL Team and can no longer be reopened.';
                                wrap.appendChild(b);
                            } else {
                                mk('Reopen (' + usedCount + '/' + maxCount + ')', 'btn btn-outline-secondary', this.onClickReopen);
                            }
                        }
                        return wrap;
                    }

                    if (!isAwaitingDealer(params.data)) return '';

                    // Set by requeueAfterRejection() (backend) the instant AL rejects this LR's
                    // previous NDV - without this the row would look identical to one that was
                    // simply never actioned. Purely informational; eligibility is already decided
                    // above by dealer_confirmation_status being absent.
                    if (params.data.dealer_confirmation_rejection_pending) {
                        var badge = document.createElement('span');
                        badge.className = 'badge badge-danger';
                        badge.style.marginRight = '6px'; badge.style.fontSize = '11px';
                        badge.innerText = 'Rejected - Resubmission Required';
                        wrap.appendChild(badge);
                    }

                    mk('Clean', 'btn btn-success', this.onClickClean);
                    mk('Shortage', 'btn btn-warning', this.onClickShortage);
                    mk('Damage', 'btn btn-danger', this.onClickDamage);
                    return wrap;
                }
            },
        ];

        return (
            <div className="container-fluid">
                <div className="d-sm-flex align-items-center justify-content-between mb-4">
                    <h1 className="col-xl-12 h3 mb-0 text-gray-800">Dealer Confirmation</h1>
                </div>

                <SweetAlert show={this.state.show} type={this.state.basicType} title={this.state.basicTitle} onConfirm={this.closeAlert}>
                </SweetAlert>

                {!options.enabled ?
                    <div className="alert alert-warning">Dealer confirmation is not currently enabled.</div>
                    : null}

                <div className="row">
                    <div className="col-xl-12 col-lg-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="ag-theme-balham" style={{ width: '100%', height: '70vh' }}>
                                    <AgGridReact
                                        modules={AllModules}
                                        columnDefs={columnDefs}
                                        rowData={rowData}
                                        defaultColDef={{ resizable: true, sortable: true, filter: true }}
                                        onGridReady={(params) => { this.gridApi = params.api; this.gridColumnApi = params.columnApi; }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Clean - mandatory confirmation modal. Clicking Clean in the grid only opens
                    this; the API call happens exclusively from "Confirm" below. Cancel (and the
                    backdrop / X) call onCancelClean, which never touches the backend. */}
                {this.state.cleanModalShow ?
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={this.state.cleanSubmitting ? undefined : this.onCancelClean}>
                        <div className="modal-dialog" style={{ maxWidth: 420, width: '90%', margin: 0 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirm Clean</h5>
                                    <button type="button" className="close" onClick={this.onCancelClean} disabled={this.state.cleanSubmitting}><span>&times;</span></button>
                                </div>
                                <div className="modal-body">
                                    <p className="mb-0">
                                        Are you sure you want to mark LR <b>{this.state.cleanRow ? this.state.cleanRow.gc_waybill_no : ''}</b> as Clean?
                                    </p>
                                    {this.state.cleanError ? <div className="text-danger mb-0 mt-2">{this.state.cleanError}</div> : null}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={this.onCancelClean} disabled={this.state.cleanSubmitting}>Cancel</button>
                                    <button type="button" className="btn btn-success" onClick={this.onConfirmClean} disabled={this.state.cleanSubmitting}>
                                        {this.state.cleanSubmitting ? 'Submitting...' : 'Confirm Clean'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    : null}

                {/* Shortage - mandatory remarks confirmation modal */}
                {this.state.shortageModalShow ?
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={this.onCancelShortage}>
                        <div className="modal-dialog" style={{ maxWidth: 480, width: '90%', margin: 0 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Report Shortage</h5>
                                    <button type="button" className="close" onClick={this.onCancelShortage} disabled={this.state.shortageSubmitting}><span>&times;</span></button>
                                </div>
                                <div className="modal-body">
                                    {this.state.shortageRow ? <p className="mb-2"><b>LR Number:</b> {this.state.shortageRow.gc_waybill_no}</p> : null}
                                    {this.state.shortageDraftRestored ?
                                        <div className="alert alert-info py-1 px-2 mb-2" style={{ fontSize: '12px' }}>
                                            A previously unsaved draft was restored.
                                        </div>
                                        : null}
                                    <div className="form-group mb-1">
                                        <label>Shortage Quantity <span style={{ color: 'red' }}>*</span></label>
                                        <input type="number" min="1" step="1" className="form-control"
                                            value={this.state.shortageQuantity} onChange={this.onChangeShortageQuantity}
                                            disabled={this.state.shortageSubmitting} placeholder="e.g. 3" />
                                        <small className="form-text text-muted">Number of pieces/cartons short - not the same as NDV Amount below.</small>
                                    </div>
                                    <div className="form-group mb-1">
                                        <label>NDV Amount (Rs.) <span style={{ color: 'red' }}>*</span></label>
                                        <input type="number" min="0" step="0.01" className="form-control"
                                            value={this.state.shortageNdvAmount} onChange={this.onChangeShortageNdvAmount}
                                            disabled={this.state.shortageSubmitting} placeholder="0.00" />
                                    </div>
                                    <div className="form-group mb-1">
                                        <label>Remarks <span style={{ color: 'red' }}>*</span></label>
                                        <textarea className="form-control" rows="4" maxLength={this.state.options.max_remarks_length}
                                            value={this.state.shortageRemarks} onChange={this.onChangeShortageRemarks}
                                            disabled={this.state.shortageSubmitting} placeholder="Describe the shortage..." autoFocus />
                                        <small className="form-text text-muted">{this.state.shortageRemarks.length} / {this.state.options.max_remarks_length} characters</small>
                                    </div>
                                    {this.state.shortageError ? <div className="text-danger mb-1">{this.state.shortageError}</div> : null}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={this.onCancelShortage} disabled={this.state.shortageSubmitting}>Cancel</button>
                                    <button type="button" className="btn btn-warning" onClick={this.onConfirmShortage}
                                        disabled={this.state.shortageSubmitting || !this.state.shortageRemarks.trim() || this.state.shortageNdvAmount === '' || this.state.shortageQuantity === ''}>
                                        {this.state.shortageSubmitting ? 'Submitting...' : 'Submit Shortage'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    : null}

                {/* Damage - mandatory image upload confirmation modal */}
                {this.state.damageModalShow ?
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={this.onCancelDamage}>
                        <div className="modal-dialog" style={{ maxWidth: 480, width: '90%', margin: 0 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Report Damage</h5>
                                    <button type="button" className="close" onClick={this.onCancelDamage} disabled={this.state.damageSubmitting}><span>&times;</span></button>
                                </div>
                                <div className="modal-body">
                                    {this.state.damageRow ? <p className="mb-2"><b>LR Number:</b> {this.state.damageRow.gc_waybill_no}</p> : null}
                                    {this.state.damageDraftRestored ?
                                        <div className="alert alert-info py-1 px-2 mb-2" style={{ fontSize: '12px' }}>
                                            A previously unsaved draft was restored ({this.state.damageDraftImageCount} image(s) were
                                            selected before - please re-select them, as files themselves are never saved in a draft).
                                        </div>
                                        : null}
                                    <div className="form-group mb-1">
                                        <label>Damage Images <span style={{ color: 'red' }}>*</span></label>
                                        <input type="file" className="form-control" multiple accept="image/jpeg,image/png,image/webp"
                                            onChange={this.onChangeDamageFiles} disabled={this.state.damageSubmitting} />
                                        <small className="form-text text-muted">{this.state.damageFiles.length} file(s) selected</small>
                                    </div>
                                    <div className="form-group mb-1">
                                        <label>NDV Amount (Rs.) <span style={{ color: 'red' }}>*</span></label>
                                        <input type="number" min="0" step="0.01" className="form-control"
                                            value={this.state.damageNdvAmount} onChange={this.onChangeDamageNdvAmount}
                                            disabled={this.state.damageSubmitting} placeholder="0.00" />
                                    </div>
                                    <div className="form-group mb-1">
                                        <label>Remarks (optional)</label>
                                        <textarea className="form-control" rows="3" maxLength={this.state.options.max_remarks_length}
                                            value={this.state.damageRemarks} onChange={this.onChangeDamageRemarks} disabled={this.state.damageSubmitting} />
                                    </div>
                                    {this.state.damageError ? <div className="text-danger mb-1">{this.state.damageError}</div> : null}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={this.onCancelDamage} disabled={this.state.damageSubmitting}>Cancel</button>
                                    <button type="button" className="btn btn-danger" onClick={this.onConfirmDamage} disabled={this.state.damageSubmitting || this.state.damageNdvAmount === ''}>
                                        {this.state.damageSubmitting ? 'Submitting...' : 'Submit Damage'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    : null}

                {/* WEB-025 - Reopen confirmation modal */}
                {this.state.reopenModalShow ?
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={this.onCancelReopen}>
                        <div className="modal-dialog" style={{ maxWidth: 480, width: '90%', margin: 0 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Reopen Confirmation</h5>
                                    <button type="button" className="close" onClick={this.onCancelReopen} disabled={this.state.reopenSubmitting}><span>&times;</span></button>
                                </div>
                                <div className="modal-body">
                                    {this.state.reopenRow ?
                                        <p className="mb-2">
                                            <b>LR Number:</b> {this.state.reopenRow.gc_waybill_no}<br />
                                            <b>Current Condition:</b> {this.state.reopenRow.dealer_confirmation_condition}
                                        </p>
                                        : null}
                                    <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: '12px' }}>
                                        This will reopen the confirmation so a corrected condition can be submitted. The
                                        original confirmation is preserved in history, not deleted.
                                    </div>
                                    <div className="form-group mb-1">
                                        <label>Reason for Reopening {this.state.options.condition_change.require_reopen_remarks ? <span style={{ color: 'red' }}>*</span> : '(optional)'}</label>
                                        <textarea className="form-control" rows="3" maxLength={this.state.options.max_remarks_length}
                                            value={this.state.reopenRemarks} onChange={this.onChangeReopenRemarks}
                                            disabled={this.state.reopenSubmitting} placeholder="Why is this being reopened?" autoFocus />
                                        <small className="form-text text-muted">{this.state.reopenRemarks.length} / {this.state.options.max_remarks_length} characters</small>
                                    </div>
                                    {this.state.reopenError ? <div className="text-danger mb-1">{this.state.reopenError}</div> : null}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={this.onCancelReopen} disabled={this.state.reopenSubmitting}>Cancel</button>
                                    <button type="button" className="btn btn-outline-secondary" onClick={this.onConfirmReopen}
                                        disabled={this.state.reopenSubmitting || (this.state.options.condition_change.require_reopen_remarks && !this.state.reopenRemarks.trim())}>
                                        {this.state.reopenSubmitting ? 'Reopening...' : 'Reopen'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    : null}
            </div>
        );
    }
}
